import axios from 'axios';
import apiConfig from '../config/apiConfig';

const API_BASE_URL = apiConfig.apiBaseURL;

// Contact interface
export interface Contact {
  _id?: string;
  name: string;
  relationship: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  email: string;
  phoneNumber: string;
  alternatePhoneNumber?: string;
  profileUrl?: string;
  isPrimaryLegacyContact?: boolean;
  isSecondaryLegacyContact?: boolean;
  createdAt?: string;
  updatedAt?: string;
  deleted?: boolean;
  oldImage: string
}

// API response interfaces
export interface ContactResponse {
  status: string;
  message: string;
  contact?: Contact;
  data?: Contact[];
  pagination?: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  };
}

// Parameters for getAllContacts
export interface GetContactsParams {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  relationship?: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

const getAuthHeadersForFormData = () => {
  const token = localStorage.getItem('authToken');
  return {
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const contactApi = {
  // Get all contacts with pagination, search, and filtering
  getAllContacts: async (params: GetContactsParams = {}): Promise<ContactResponse> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.search) queryParams.append('search', params.search);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await axios.get(
        `${API_BASE_URL}/contacts/get-all-contacts?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching contacts:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch contacts',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get contact by ID
  getContactById: async (contactId: string): Promise<ContactResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/contacts/get-contact/${contactId}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch contact');
    }
  },

  // Add new contact
  addContact: async (contactData: Omit<Contact, '_id'>, avatar?: File): Promise<ContactResponse> => {
    try {
      const formData = new FormData();
      
      // Append contact data
      Object.entries(contactData).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          formData.append(key, value.toString());
        }
      });
      
      // Append avatar if provided
      if (avatar) {
        formData.append('avatar', avatar);
      }

      const response = await axios.post(
        `${API_BASE_URL}/contacts/add-contact`,
        formData,
        {
          headers: getAuthHeadersForFormData()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to add contact');
    }
  },

  // Update contact
 updateContact: async (contactId: string, contactData: Partial<Contact>, avatar?: File): Promise<ContactResponse> => {
  try {
    const formData = new FormData();
    console.log("contactData in api:", contactData)
    // Append contact data
    Object.entries(contactData).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, value.toString());
      }else if(key === "oldImage" && value === ''){
        formData.append(key, '');
      }
    });
    
    // Append avatar if provided
    if (avatar) {
      formData.append('avatar', avatar);
    }

    const response = await axios.put(
      `${API_BASE_URL}/contacts/update-contact/${contactId}`,
      formData,
      {
        headers: getAuthHeadersForFormData()  // Use FormData headers, not JSON headers
      }
    );
    
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to update contact');
  }
},

  // Delete contact (soft delete)
  deleteContact: async (contactId: string): Promise<ContactResponse> => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/contacts/soft-delete-contact/${contactId}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete contact');
    }
  },

  // Set legacy contact (primary or secondary)
  setLegacyContact: async (contactId: string, type: 'primary' | 'secondary'): Promise<ContactResponse> => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/contacts/set-legacy-contact`,
        { contactId, type },
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to set legacy contact');
    }
  },

  // Get contact names (for dropdowns/autocomplete)
  getContactNames: async (): Promise<ContactResponse> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/contacts/get-contact-names`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch contact names');
    }
  }
};